import { has, includes, isEmpty } from 'lodash-es';

import {
  VALIDATION_MAP,
  isFieldValueValid,
  getValidationErrorMessage,
  CONDITIONS_QUESTIONS_SECTION_INPUT,
} from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { NOT_VALID_CONDITION_LIST } from 'site-modules/shared/constants/appraisal/appraisal';

/**
 * Gets validationErrors map for step with passed id.
 * @param {string} activeStepId
 * @param {Object} state
 * @param {Boolean} isExtendedReport
 * @returns {Map}
 */
export function getValidationErrors(activeStepId, state, isExtendedReport) {
  const {
    zipCode,
    vehicle,
    exteriorColor,
    interiorColor,
    mileage,
    condition,
    isZipCodeValid,
    partnerQuestionnaireVerboseResponse,
    transmissionType,
    hasExteriorColorsOptions,
  } = state;
  const make = vehicle?.make;
  const model = vehicle?.model;
  const year = vehicle?.year;
  const style = vehicle?.style;
  const exteriorColorId = exteriorColor?.id;
  const interiorColorId = interiorColor?.id;
  const fields = {
    make,
    model,
    year,
    zipCode,
    style,
    interiorColor: interiorColorId,
    mileage,
    condition,
    [CONDITIONS_QUESTIONS_SECTION_INPUT]: partnerQuestionnaireVerboseResponse,
  };
  if (isExtendedReport) {
    fields.transmissionType = transmissionType;

    if (hasExteriorColorsOptions) {
      fields.exteriorColor = exteriorColorId;
    }
  }
  const additionalParametersForValidation = {
    styleYear: style?.year,
  };

  const validationErrors = new Map();

  if (VALIDATION_MAP[activeStepId]) {
    VALIDATION_MAP[activeStepId].forEach(({ fieldName }) => {
      const isZipCodeInvalid = fieldName === 'zipCode' && !isZipCodeValid;
      if (
        (has(fields, fieldName) &&
          !isFieldValueValid(activeStepId, fieldName, fields[fieldName], additionalParametersForValidation)) ||
        isZipCodeInvalid
      ) {
        const fieldValue = isZipCodeInvalid ? '' : fields[fieldName];
        validationErrors.set(
          fieldName,
          getValidationErrorMessage(activeStepId, fieldName, fieldValue, additionalParametersForValidation)
        );
      }
    });
  }

  return validationErrors;
}

/**
 * Re-validates errorMap for the given step if errors are currently displayed.
 * Returns state unchanged if no errors are present.
 * @param {string} stepId
 * @param {Object} state
 * @param {Boolean} isExtendedReport
 * @returns {Object} state with updated errorMap
 */
export function revalidateErrors(stepId, state, isExtendedReport) {
  if (state.errorMap.size) {
    return { ...state, errorMap: getValidationErrors(stepId, state, isExtendedReport) };
  }
  return state;
}

/**
 * Determine whether Location and Style step is valid.
 * @param {Object} state
 * @returns {boolean}
 */
export function isLocationAndStyleStepValid(state) {
  return state.isZipCodeValid && !isEmpty(state.vehicle.make) && !isEmpty(state.vehicle.style);
}

/**
 * Determine whether Colors and Options step is valid.
 * @param {Object} state
 * @param {boolean} isExtendedReport
 * @returns {boolean}
 */
export function isColorsAndOptionsStepValid(state, isExtendedReport) {
  const { hasExteriorColorsOptions, exteriorColor } = state;

  const isExteriorColorError = isExtendedReport && hasExteriorColorsOptions ? !exteriorColor?.id : false;

  return !isExteriorColorError;
}

/**
 * Determine whether Condition and Mileage step is valid.
 * @param {Object} state
 * @returns {boolean}
 */
export function isConditionAndMileageStepValid(state) {
  return !!(
    state.mileage &&
    state.isMileageValid &&
    state.condition &&
    !includes(NOT_VALID_CONDITION_LIST, state.condition)
  );
}

/**
 * Determines whether offer condition step is valid.
 * @param {Object} state
 * @returns {boolean}
 */
export function isPartnerOfferConditionsStepValid(state) {
  return state.areQuestionnaireResponsesValid;
}

/**
 * Determines whether license plate step is valid.
 * @param {Object} state
 * @returns {boolean}
 */
export function isLicensePlateAndVinStepValid(state) {
  const { vin, isVinValid, hasVinMismatchError } = state;
  return !!(vin && isVinValid && !hasVinMismatchError);
}

/**
 * Checks that all steps except final are completed.
 * @param {Array} steps
 * @returns {boolean}
 */
export function checkStepsCompleteness(steps) {
  const stepsLength = steps.length;
  return steps.slice(0, stepsLength - 1).every(step => step.isDone);
}
